import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { gql } from '@apollo/client/core';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Permission } from '@app/core/model/client/permission';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import {
  CreateOrganizationInput,
  Organization,
  OrganizationWithLogo,
  UpdateOrganizationInput
} from '@app/core/model/entities/organization/organization';
import { UserPreferences } from '@app/core/model/entities/preferences/user-preferences';
import {
  DuplicateOrganizationModalComponent
} from '@app/features/main/views/_tb/tb-organizations/tb-organizations-datagrid/duplicate-organization-modal/duplicate-organization-modal.component';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { getLocale, thenReturn } from '@app/shared/extra/utils';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import { UserPreferencesService } from '@app/shared/services/user-preferences.service';
import { TranslateService } from '@ngx-translate/core';
import ApiService from '@services/api.service';
import { ClientThemingService } from '@services/client-theming.service';
import { FileService } from '@services/file.service';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import { plainToInstance } from 'class-transformer';
import { combineLatest, EMPTY, expand, from, Observable, of, Subject } from 'rxjs';
import { catchError, map, mergeMap, reduce, switchMap, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class OrganizationsService {
  private readonly appManager = inject(AppManager);
  private readonly accessManager = inject(AccessManager);
  private readonly userPreferencesService = inject(UserPreferencesService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly appConfig = inject(AppConfig);
  private readonly documentsService = inject(DocumentsService);
  private readonly fileService = inject(FileService);
  private readonly apiService = inject(ApiService);
  private readonly popupManager = inject(PopupManager);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly translate = inject(TranslateService);
  private readonly clientTheming = inject(ClientThemingService);

  // Store multiple organizations.
  public organizations: Organization[] = [];

  private addOrganizationSubject = new Subject<Organization>();
  private deleteOrganizationSubject = new Subject<Organization>();

  /**
   * GraphQL fragment for fetching organization information.
   * - id: Unique identifier, used to navigate to its dashboard page.
   * - name: Name of the organization, used for display.
   * - properties: Any additional properties, used to get the ID of the associated logo image
   * @private
   */
  private readonly organizationsInfoGraphqlFragment = gql`
    fragment OrganizationInfo on Organization {
      id
      name
      locale
      currency
      organizationType
      properties
      computedProperties
      isDemo
      isTb
      creationDate
      storageSpace
    }
  `;

  /**
   * Emits an Organization after it has been created.
   */
  public get organizationCreated$(): Observable<Organization> {
    return this.addOrganizationSubject.asObservable();
  }

  /**
   * Emits an Organization after it has been deleted.
   */
  public get organizationDeleted$(): Observable<Organization> {
    return this.deleteOrganizationSubject.asObservable();
  }

  /**
   * Load an Organization, as well as related settings such as the current User's preferences, permissions, and modules.
   * @param organizationId ID of the Organization.
   * @return Observable that emits the loaded Organization.
   */
  public loadOrganization(organizationId: string): Observable<Organization> {
    const query = gql`
      query Organization($orgId: String!) {
        organization(id: $orgId) {
          ...OrganizationInfo
        }
        permissionsForUserOrg(organizationId: $orgId) {
          id
          code
        }
        userPreferences(organizationId: $orgId) {
          id
          preferences
        }
      }
      ${this.organizationsInfoGraphqlFragment}
    `;
    const variables = {
      orgId: organizationId
    };

    return combineLatest([
      this.translate.use(organizationId).pipe(
        map(() => true),
        catchError(() => of(false))
      ),
      this.apiService.query({query, variables})
        .pipe(
          map(data => ({
            organization: plainToInstance(Organization, data['organization'] as Organization),
            permissions: plainToInstance(Permission, data['permissionsForUserOrg'] as Permission[]),
            preferences: plainToInstance(UserPreferences, data['userPreferences'] as UserPreferences),
          }))
        )
    ]).pipe(
      switchMap(([orgFileExists, {organization, permissions, preferences}]) => {
        // Set the only organization as current
        this.appManager.emptyCurrentEntityStack();
        this.appManager.currentOrganization = organization;
        this.appManager.entityObservable$.next(true);

        // Set the user permissions once the organization is selected
        this.accessManager.currentUser.organizationPermissions = permissions;

        // Set the user preferences once the organization is selected
        this.userPreferencesService.currentUserPreferences = preferences;

        // Attempt to load a custom theming file for the organization
        this.clientTheming.loadOrganizationCss(organization.id);

        // Register the org translation file
        this.translate.addLangs([organization.id]);

        // Apply translation strategy based on org file existence and user language preference
        const userLanguage = this.userPreferencesService.currentUserPreferences?.preferences?.['language'];
        const browserLang = getLocale().substring(0, 2);
        const validUserLanguage = ['fr', 'en'].includes(userLanguage);

        if (orgFileExists) {
          // Org is already the active lang (translate.use(orgId) succeeded above).
          // setDefaultLang is safe here — called after translate.use(orgId) completed.
          const defaultLang = validUserLanguage ? userLanguage : browserLang;
          this.translate.setDefaultLang(defaultLang);
          return of(organization);
        } else if (validUserLanguage) {
          // No org file: use user language preference as active, browser as fallback
          this.translate.setDefaultLang(browserLang);
          return this.appManager.useLang(userLanguage).pipe(thenReturn(organization));
        } else {
          // No org file, no preference: fall back to browser language
          return this.appManager.resetLang().pipe(thenReturn(organization));
        }
      })
    );
  }

  /**
   * Loads an organization page.
   *
   * This function performs a GraphQL query to fetch organizations based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Organization objects.
   */
  private loadOrganizationsPage(selectParams: SelectParams): Observable<Organization[]> {
    const query = gql`
      query AccessibleOrganizations($selectParams: SelectParams!) {
        organizations(selectParams: $selectParams) {
          ...OrganizationInfo
        }
      }
      ${this.organizationsInfoGraphqlFragment}
    `;
    const variables = {
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Organization, data['organizations'] as Organization[])));
  }

  /**
   * Loads all accessible organization pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch organizations based on the provided select parameters.
   * The select parameters include sorting, filtering, and grouping.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Organization objects.
   */
  private loadAllOrganizations(selectParams: SelectParams): Observable<Organization[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};

    return this.loadOrganizationsPage(params)
      .pipe(
        expand((organizations, i) => {
          if (organizations.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadOrganizationsPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Organization[])
      );
  }

  /**
   * Loads a list of accessible organizations based on the given select parameters.
   * Clears the current organizations and retrieves a new set of organizations.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Organization objects.
   */
  public loadData(selectParams: SelectParams): Observable<Organization[]> {
    this.organizations = [];
    return this.loadAllOrganizations(selectParams)
      .pipe(tap(organizations => this.organizations = organizations));
  }


  /**
   * Fetch all accessible by the User Organizations with their associated logos sorted by name.
   * @return Observable emitting a list of Organizations with their associated logos.
   */
  public loadOrganizationsWithLogo(): Observable<OrganizationWithLogo[]> {
    return this.loadAllOrganizations({
      sorts: [{field: 'name', direction: 'ASC'}],
      locale: getLocale()
    })
      .pipe(
        switchMap(organizations => {
          return from(organizations)
            .pipe(
              mergeMap(organization => {
                if (!organization.logoDocumentId) {
                  // If the organization doesn't have a logo, only return the organization with a placeholder logo.
                  return of({organization, logoUrl: 'url(' + this.appConfig.PLACEHOLDER_ORGANIZATION + ')'});
                } else {
                  // If the organization has a logo, Load the logo related to the organization.
                  return this.documentsService.document(organization.logoDocumentId)
                    .pipe(
                      // Map the logo document to a style url.
                      switchMap(document => {
                        return this.fileService.getDocumentFileUrl(document.id).pipe(
                          map(documentUrl => {
                            const logoUrl = this.sanitizer.bypassSecurityTrustStyle(`url(${documentUrl})`);
                            return {organization, logoUrl};
                          })
                        );
                      })
                    );
                }
              })
            );
        }),
        toArray()
      );
  }

  /**
   * Fetch the storage information of an Organization.
   * @param organizationId ID of the Organization.
   * @return An Observable that emits the storage config of the Organization.
   */
  public getOrganizationStorage(organizationId: string): Observable<{ occupiedStorageSpace: number }> {
    const query = gql`
      query Organization($organizationId: String!) {
        currentlyUsedStorageSpace(organizationId: $organizationId)
      }
    `;
    const variables = {organizationId};
    return this.apiService.query({query, variables})
      .pipe(
        map(data => {
          return {
            occupiedStorageSpace: data['currentlyUsedStorageSpace'] as number
          };
        })
      );
  }


  /**
   * Call the API to create a new organization with the input data.
   * @param organizationInput Organization input data.
   * @return Observable that will emit the new organization.
   */
  public createOrganization(organizationInput: CreateOrganizationInput): Observable<Organization> {
    const mutation = gql`
      mutation CreateOrganization($input: CreateOrganizationInput!) {
        createOrganization(organizationInput: $input) {
          ...OrganizationInfo
        }
      }
      ${this.organizationsInfoGraphqlFragment}
    `;
    const variables = {input: organizationInput};
    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(Organization, data['createOrganization'])));
  }

  /**
   * API call to update an organization.
   * @param organizationId the Organization ID
   * @param organizationInput the data to update
   * @return the updated Organization
   */
  public updateOrganization(
    organizationId: string,
    organizationInput: UpdateOrganizationInput
  ): Observable<Organization> {
    const mutation = gql`
      mutation UpdateOrganization($organizationInput: UpdateOrganizationInput!) {
        updateOrganization(organizationInput: $organizationInput) {
          ...OrganizationInfo
        }
      }
      ${this.organizationsInfoGraphqlFragment}
    `;
    const variables = {
      organizationInput: {id: organizationId, ...organizationInput}
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Organization, data['updateOrganization'])),
        tap(organization => this.appManager.currentOrganization = organization)
      );
  }

  /**
   * Open a dialog to delete a Organization.
   * @param organization Entity to delete.
   */
  public openDeleteOrganizationDialog(organization: Organization): void {
    const dialogRef = this.popupManager.showOkCancelPopup({
      dialogTitle: this.translate.instant('TITLE.DELETE_ORGANIZATION'),
      dialogMessage: this.translate.instant('MESSAGE.DELETE_ORGANIZATION'),
      okText: this.translate.instant('BUTTON.DELETE_ORGANIZATION'),
      type: 'warning'
    });
    dialogRef.afterClosed().subscribe((dialogResponse) => {
      const analyticsEvent = new AnalyticsEvent(ActionEnum.DELETE, EntityTypeEnum.ORGANIZATION);
      if (dialogResponse === 'yes') {
        analyticsEvent.addProperties({
          [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE,
          [AnalyticsKeyEnum.ENTITY_ID]: organization.id
        });
        this.deleteOrganization(organization);
      } else {
        analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
      }
      this.analyticsService.trackEvent(analyticsEvent);
    });
  }

  /**
   * Delete an existing organization.
   * @param organization Entity to delete.
   */
  public deleteOrganization(organization: Organization): void {
    this.snackbarManager.showInfiniteSnackbar(this.translate.instant('LABEL.DELETE_IN_PROGRESS'));
    this.deleteOrganizationSubject.next(organization);

    const query = gql`
      subscription DeleteOrganization($id: String!) {
        deleteOrganization(id: $id)
      }
    `;
    const variables = {
      id: organization.id
    };
    this.apiService.subscribe({query, variables})
      .subscribe({
        next: data => {
          if (data['deleteOrganization']) {
            this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.DELETE_ORGANIZATION'));
          } else this.snackbarManager.closeSnackbar();
        },
        error: () => {
          this.snackbarManager.closeSnackbar();
        }
      });
  }

  /**
   * Open a dialog to duplicate an existing organization.
   * @param organization to duplicate.
   */
  public openDuplicateOrganizationDialog(organization: Organization): void {
    const dialogRef = this.popupManager.showGenericPopup(DuplicateOrganizationModalComponent, PopupSize.SMALL, {});
    dialogRef.afterClosed().subscribe((dialogResponse) => {
      const analyticsEvent = new AnalyticsEvent(ActionEnum.DUPLICATE, EntityTypeEnum.ORGANIZATION);
      if (dialogResponse === 'yes') {
        analyticsEvent.addProperties({
          [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE,
          [AnalyticsKeyEnum.ENTITY_ID]: organization.id
        });
        const dataObject = dialogRef.componentInstance.getGeneratedObject();
        this.duplicateOrganization(organization.id, dataObject.organizationName, dataObject.duplicateData);
      } else {
        analyticsEvent.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
      }
      this.analyticsService.trackEvent(analyticsEvent);
    });
  }

  /**
   * Duplicate an existing organization.
   * @param organizationId The ID of the organization to duplicate.
   * @param name of the new organization.
   * @param duplicateData True if organization's data must be duplicated, false otherwise. Default true.
   */
  private duplicateOrganization(organizationId: string, name: string, duplicateData: boolean = true): void {
    const query = gql`
      subscription DuplicateOrganization($organizationId: String!, $name: String!, $duplicateData: Boolean) {
        duplicateOrganization(name: $name, id: $organizationId, duplicateData: $duplicateData) {
          ...OrganizationInfo
        }
      }
      ${this.organizationsInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: organizationId,
      name: name,
      duplicateData
    };

    this.snackbarManager.showInfiniteSnackbar(this.translate.instant('LABEL.DUPLICATE_IN_PROGRESS'));

    this.apiService.subscribe({query, variables})
      .pipe(map(data => plainToInstance(Organization, data['duplicateOrganization'])))
      .subscribe({
        next: organization => this.addOrganizationSubject.next(organization),
        complete: () =>
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.DUPLICATE_ORGANIZATION')),
        error: () => this.snackbarManager.closeSnackbar()
      });
  }
}
